import { createTheme, Drawer, MantineThemeProvider, Modal, Notification } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Accessible names of Mantine's icon-only controls — the «×» of dialogs, drawers and toasts, the
 * calendar arrows — in the UI language. The app theme is static, so they are layered over it here
 * (`t` changes with the language, and so does this theme).
 */
export function MantineLabelsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  const theme = useMemo(() => {
    const closeButtonProps = { 'aria-label': t('actions.close') };
    return createTheme({
      components: {
        Drawer: Drawer.extend({ defaultProps: { closeButtonProps } }),
        Modal: Modal.extend({ defaultProps: { closeButtonProps } }),
        Notification: Notification.extend({ defaultProps: { closeButtonProps } }),
        DatePickerInput: DatePickerInput.extend({
          defaultProps: {
            ariaLabels: {
              previousMonth: t('calendar.previousMonth'),
              nextMonth: t('calendar.nextMonth'),
              previousYear: t('calendar.previousYear'),
              nextYear: t('calendar.nextYear'),
              previousDecade: t('calendar.previousDecade'),
              nextDecade: t('calendar.nextDecade'),
            },
          },
        }),
      },
    });
  }, [t]);

  return <MantineThemeProvider theme={theme}>{children}</MantineThemeProvider>;
}
